import React, {CSSProperties, useEffect, useRef, useState} from 'react'

import {Alert, Button, Colors, Spinner} from '../../src/components/UI/index.js'
import {toJpegDataUrl} from '../utils/imageToJpeg.js'
import utils from '../utils/utils.js'
import t from 'src/helpers/Languages.js'

type CameraScreenProps = {
    /** number of photos already added to the address */
    imageCount: number
    maxImages: number
    /** called with the photos taken in this camera screen */
    onComplete: (images: string[]) => void
    /** closes the camera screen and discards the photos taken in it */
    onCancel: () => void
}

type TorchConstraintSet = NonNullable<Parameters<MediaStreamTrack['applyConstraints']>[0]['advanced']>[number] & {torch?: boolean}

function getErrorMessage(err: unknown) {
    const name = err instanceof Error ? err.name : ''

    if (name === 'NotAllowedError' || name === 'SecurityError') {
        return t('cameraPermissionDenied')
    }

    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        return t('cameraNotFound')
    }

    if (name === 'NotReadableError' || name === 'AbortError') {
        return t('cameraInUse')
    }

    return t('cameraCouldNotStart')
}

/**
 * Camera inside the messenger app. The phone's camera app sends the browser to the background, and Android
 * may then close the tab under memory pressure, losing the photo. This keeps the browser in the foreground.
 */
export default function CameraScreen({imageCount, maxImages, onComplete, onCancel}: CameraScreenProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const trackRef = useRef<MediaStreamTrack>(null)

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [torchSupported, setTorchSupported] = useState(false)
    const [torchOn, setTorchOn] = useState(false)
    const [flash, setFlash] = useState(false)
    const [images, setImages] = useState<string[]>([])

    const totalCount = imageCount + images.length

    useEffect(() => {
        let stream: MediaStream | null = null
        let cancelled = false

        const start = async () => {
            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    throw new Error('getUserMedia is not supported')
                }

                stream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {facingMode: {ideal: 'environment'}, width: {ideal: 1920}, height: {ideal: 1920}}
                })

                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop())
                    return
                }

                const [track] = stream.getVideoTracks()
                trackRef.current = track

                const capabilities = track.getCapabilities?.() as ReturnType<MediaStreamTrack['getCapabilities']> & {torch?: boolean}
                setTorchSupported(!!capabilities?.torch)

                videoRef.current.srcObject = stream
                await videoRef.current.play()

                if (!cancelled) {
                    setLoading(false)
                }
            } catch (err) {
                console.error(err)

                if (!cancelled) {
                    setLoading(false)
                    setError(getErrorMessage(err))
                }
            }
        }

        start()

        return () => {
            cancelled = true

            // Stops the camera, which also turns off the torch and the camera indicator
            stream?.getTracks().forEach((track) => track.stop())
            trackRef.current = null

            if (videoRef.current) {
                videoRef.current.srcObject = null
            }
        }
    }, [])

    const onToggleTorch = async () => {
        try {
            await trackRef.current?.applyConstraints({advanced: [{torch: !torchOn} as TorchConstraintSet]})
            setTorchOn(!torchOn)
        } catch (err) {
            console.error(err)
            setTorchSupported(false)
        }
    }

    const onTakePicture = () => {
        const video = videoRef.current

        if (!video?.videoWidth || !video?.videoHeight || totalCount >= maxImages) {
            return
        }

        const newImages = [...images, toJpegDataUrl(video, video.videoWidth, video.videoHeight)]

        if (imageCount + newImages.length >= maxImages) {
            onComplete(newImages)
            return
        }

        setImages(newImages)
        setFlash(true)
        setTimeout(() => setFlash(false), 150)
    }

    const roundButtonStyle: CSSProperties = {
        width: 56,
        height: 56,
        borderRadius: '50%',
        border: 'none',
        background: 'rgba(255, 255, 255, 0.2)',
        color: Colors.white,
        fontSize: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
    }

    return (
        <div style={{position: 'fixed', inset: 0, zIndex: 1020, background: 'black', display: 'flex', flexDirection: 'column'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px'}}>
                <div style={{width: 56}} />

                <span style={{color: Colors.white, fontSize: 16}}>
                    {`${totalCount}/${maxImages} ${t('photos')}`}
                </span>

                {torchSupported ?
                    <button
                        type='button'
                        aria-label={t('cameraTorch')}
                        style={{...roundButtonStyle, background: torchOn ? Colors.white : roundButtonStyle.background, color: torchOn ? 'black' : Colors.white}}
                        onClick={onToggleTorch}
                    >
                        <i className={torchOn ? 'mdi mdi-flashlight' : 'mdi mdi-flashlight-off'} />
                    </button> :
                    <div style={{width: 56}} />
                }
            </div>

            {images.length > 0 &&
                <div style={{display: 'flex', gap: 8, padding: '0 16px 12px 16px', overflowX: 'auto', flexShrink: 0}}>
                    {images.map((image, index) => (
                        <div key={index} style={{position: 'relative', flexShrink: 0}}>
                            <img
                                src={image}
                                style={{height: 70, maxWidth: 115, objectFit: 'contain', borderRadius: 4, display: 'block'}}
                            />

                            <button
                                type='button'
                                aria-label={t('delete')}
                                style={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: 'rgba(0, 0, 0, 0.6)',
                                    color: Colors.white,
                                    fontSize: 16,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setImages(images.filter((_, i) => i !== index))}
                            >
                                <i className='mdi mdi-delete' />
                            </button>
                        </div>
                    ))}
                </div>
            }

            <div style={{flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'}}>
                <video
                    ref={videoRef}
                    playsInline
                    muted
                    style={{width: '100%', height: '100%', objectFit: 'contain', display: error ? 'none' : 'block'}}
                />

                {loading &&
                    <Spinner style={{position: 'absolute'}} color={Colors.white} />
                }

                {error &&
                    <div style={{width: '90%', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <Alert variant='warning' style={{marginBottom: 6, width: '100%'}}>{error}</Alert>

                        <Button
                            variant='outline-white'
                            icon='mdi mdi-help-circle-outline'
                            label={t('help')}
                            onClick={() => utils.onClickHelp('camera-en-locatietoestemming')}
                        />
                    </div>
                }

                {flash &&
                    <div style={{position: 'absolute', inset: 0, background: Colors.white, opacity: 0.6}} />
                }
            </div>

            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 32px'}}>
                <button
                    type='button'
                    aria-label={t('cancel')}
                    style={roundButtonStyle}
                    onClick={onCancel}
                >
                    <i className='mdi mdi-close' />
                </button>

                <button
                    type='button'
                    aria-label={t('cameraTakePicture')}
                    disabled={loading || !!error}
                    style={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        border: `4px solid ${Colors.white}`,
                        background: 'rgba(255, 255, 255, 0.3)',
                        opacity: loading || error ? 0.3 : 1,
                        cursor: 'pointer'
                    }}
                    onClick={onTakePicture}
                />

                <button
                    type='button'
                    aria-label={t('confirm')}
                    style={{...roundButtonStyle, background: images.length > 0 ? Colors.successBright : roundButtonStyle.background}}
                    onClick={() => onComplete(images)}
                >
                    <i className='mdi mdi-check' />
                </button>
            </div>
        </div>
    )
}
